import { Box, Typography } from '@mui/material';
import { LegalBlock, LegalInline, LegalList } from './utils';

interface LegalDocumentContentProps {
  blocks: LegalBlock[];
}

function renderText(text: string): (string | JSX.Element)[] {
  return text.split('\n').flatMap((part, index) => (index === 0 ? [part] : [<br key={index} />, part]));
}

function renderInlines(inlines: LegalInline[]): JSX.Element[] {
  return inlines.map((inline, index) => {
    const content = inline.bold ? <strong className="font-semibold">{renderText(inline.text)}</strong> : renderText(inline.text);

    if (inline.href) {
      return (
        <a key={index} href={inline.href} className="text-brand-primary underline underline-offset-2 hover:no-underline">
          {content}
        </a>
      );
    }

    return <span key={index}>{content}</span>;
  });
}

function renderList(list: LegalList, nested = false): JSX.Element {
  const listClassName = list.ordered
    ? `${nested ? 'mt-2' : 'mb-6'} list-decimal pl-6 marker:font-semibold`
    : `${nested ? 'mt-2' : 'mb-6'} pl-6 marker:text-brand-primary ${nested ? 'list-[circle]' : 'list-disc'}`;

  return (
    <Box component={list.ordered ? 'ol' : 'ul'} className={listClassName}>
      {list.items.map((item, itemIndex) => (
        <Box key={itemIndex} component="li" className={`text-gray-dark pl-1 text-base leading-7 ${nested ? 'mb-1' : 'mb-2'}`}>
          {renderInlines(item.inlines)}
          {item.sublist && renderList(item.sublist, true)}
        </Box>
      ))}
    </Box>
  );
}

export default function LegalDocumentContent(props: LegalDocumentContentProps): JSX.Element {
  const { blocks } = props;

  return (
    <Box component="article" className="min-w-0 max-w-3xl">
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'heading':
            return (
              <Typography
                key={block.id}
                id={block.id}
                variant={block.level === 1 ? 'h2' : 'h3'}
                component={block.level === 1 ? 'h2' : 'h3'}
                className={
                  block.level === 1
                    ? 'text-true-black mb-6 scroll-mt-28 font-serif text-2xl font-black first:mt-0 md:text-3xl'
                    : 'text-true-black mb-4 mt-10 scroll-mt-28 text-lg font-bold first:mt-0'
                }
              >
                {block.text}
              </Typography>
            );
          case 'paragraph':
            return (
              <Typography key={index} component="p" className="text-gray-dark mb-4 text-base leading-7">
                {renderInlines(block.inlines)}
              </Typography>
            );
          case 'list':
            return <Box key={index}>{renderList(block)}</Box>;
          case 'table': {
            const [headerRow, ...bodyRows] = block.rows;
            return (
              <Box key={index} className="border-grey-10 my-6 overflow-x-auto rounded-lg border">
                <Box component="table" className="w-full min-w-[560px] border-collapse text-left text-sm">
                  {headerRow && (
                    <Box component="thead">
                      <Box component="tr">
                        {headerRow.map((cell, cellIndex) => (
                          <Box
                            key={cellIndex}
                            component="th"
                            className="border-grey-10 bg-gray-light text-true-black border-b px-4 py-3 align-top font-semibold"
                          >
                            {renderInlines(cell)}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Box component="tbody">
                    {bodyRows.map((row, rowIndex) => (
                      <Box key={rowIndex} component="tr" className="last:[&>td]:border-b-0">
                        {row.map((cell, cellIndex) => (
                          <Box key={cellIndex} component="td" className="border-grey-10 text-gray-dark border-b px-4 py-3 align-top">
                            {renderInlines(cell)}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          }
          default:
            return null;
        }
      })}
    </Box>
  );
}
